import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/components/ui/accordion";
import { Input } from "@/shared/components/ui/input";
import { PasswordInput } from "@/shared/components/ui/password-input";
import { Label } from "@/shared/components/ui/label";
import { Switch } from "@/shared/components/ui/switch";
import { useTranslation } from "react-i18next";

interface ConfigState {
  baseUrl: string;
  engine: string;
  apiBaseUrl: string;
  apiKey: string;
  agentName: string;
  mailboxEnabled: boolean;
  mailbox: string;
  pollInterval: string;
  horizonDays: string;
  maxWaitMinutes: string;
  graphTenantId: string;
  graphClientId: string;
  graphClientSecret: string;
  logsApiKey: string;
  logsDirectory: string;
}

interface ConfigGeneralSectionProps {
  config: ConfigState;
  onChange: (field: string, value: string | boolean) => void;
}

export const ConfigGeneralSection = ({ config, onChange }: ConfigGeneralSectionProps) => {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-3">
        {t("assistant.config.general")}
      </p>

      <Accordion type="multiple" defaultValue={["baseUrl"]} className="w-full">
        <AccordionItem value="baseUrl">
          <AccordionTrigger className="text-sm font-medium uppercase tracking-wide">
            {t("assistant.config.baseUrl")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  {t("assistant.config.assistantBaseUrl")}
                </Label>
                <Input
                  value={config.baseUrl}
                  onChange={(e) => onChange("baseUrl", e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="apiConnection">
          <AccordionTrigger className="text-sm font-medium uppercase tracking-wide">
            {t("assistant.config.apiConnection")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.engine")}</Label>
                <Input value={config.engine} onChange={(e) => onChange("engine", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.apiBaseUrl")}</Label>
                <Input value={config.apiBaseUrl} onChange={(e) => onChange("apiBaseUrl", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.apiKey")}</Label>
                <PasswordInput value={config.apiKey} onChange={(e) => onChange("apiKey", e.target.value)} placeholder="••••••••" className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.agentName")}</Label>
                <Input value={config.agentName} onChange={(e) => onChange("agentName", e.target.value)} className="text-sm" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="calendarService">
          <AccordionTrigger className="text-sm font-medium uppercase tracking-wide">
            {t("assistant.config.calendarService")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.mailboxEnabled")}</Label>
                <Switch checked={config.mailboxEnabled} onCheckedChange={(checked) => onChange("mailboxEnabled", checked)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.mailbox")}</Label>
                <Input value={config.mailbox} onChange={(e) => onChange("mailbox", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.pollInterval")}</Label>
                <Input type="number" value={config.pollInterval} onChange={(e) => onChange("pollInterval", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.horizonDays")}</Label>
                <Input type="number" value={config.horizonDays} onChange={(e) => onChange("horizonDays", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.maxWaitMinutes")}</Label>
                <Input type="number" value={config.maxWaitMinutes} onChange={(e) => onChange("maxWaitMinutes", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.graphTenantId")}</Label>
                <Input value={config.graphTenantId} onChange={(e) => onChange("graphTenantId", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.graphClientId")}</Label>
                <Input value={config.graphClientId} onChange={(e) => onChange("graphClientId", e.target.value)} className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.graphClientSecret")}</Label>
                <PasswordInput value={config.graphClientSecret} onChange={(e) => onChange("graphClientSecret", e.target.value)} placeholder="••••••••" className="text-sm" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="logs">
          <AccordionTrigger className="text-sm font-medium uppercase tracking-wide">
            {t("assistant.config.logs")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.logsApiKey")}</Label>
                <PasswordInput value={config.logsApiKey} onChange={(e) => onChange("logsApiKey", e.target.value)} placeholder="••••••••" className="text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">{t("assistant.config.logsDirectory")}</Label>
                <Input value={config.logsDirectory} onChange={(e) => onChange("logsDirectory", e.target.value)} className="text-sm" />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
